// Added by Sejune Oh on 2019-08-22
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { inject, observer } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactInputChangeEvent, ReactKeyUpEvent } from '@/helper/define'
import Logger from '@/helper/Logger'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import PasswordCheck from './PasswordCheck'
import { UserAuthStore } from '@/stores/user/UserAuth'
import ProductStore from '@/stores/ProductStore'
import EventStore from '@/stores/EventStore'
import { PackageConfigStore } from '@/stores/mgmt/config'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface PasswordCheckCProps {
  userAuth?: UserAuthStore
  product?: ProductStore
  globalEvt?: EventStore
  packageConfig?: PackageConfigStore
  onConfirm: Function
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'product', 'globalEvt', 'packageConfig')
@observer
class PasswordCheckContainer extends Component<PasswordCheckCProps> {
  hidePassword: boolean = true
  curPwd: string = ''

  _buttonId = 'p-c-btn'
  _inputRef = createRef<HTMLInputElement>()

  constructor(props: PasswordCheckCProps) {
    super(props)
    makeObservable(this, {
      hidePassword: observable,
      curPwd: observable,
      _handlePasswordInputIconClick: action,
      _handleValueChange: action,
      userAuth: computed,
    })
  }

  render() {
    const options = {
      useLoginFormTopImage: this.props.product!.useLoginFormTopImage,
      productBI: this.props.product!.productBI,
      loginTopImage: this.props.product!.loginTopImage,
      loginBackgroundImage: this.props.product!.loginBackgroundImage,
    }

    return (
      <PasswordCheck
        inputRef={this._inputRef}
        onConfirmBtnClick={this._handleConfirm}
        onCancelBtnClick={this._handleCancel}
        userId={this.userAuth.userName!}
        onPasswordInputIconClick={this._handlePasswordInputIconClick}
        hidePassword={this.hidePassword}
        onValueChange={this._handleValueChange}
        onKeyUp={this._handleKeyUp}
        btnId={this._buttonId}
        version={process.env.REACT_APP_VER_RSC_TEXTS || 'dev'}
        copyRight={this.props.product!.copyright}
        desc={this.props.packageConfig!.getConfirmPasswordDescription()}
        {...options}
      />
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handlePasswordInputIconClick = () => {
    this.hidePassword = !this.hidePassword
  }

  _handleValueChange: ReactInputChangeEvent = ({ currentTarget: { value } }) => {
    this.curPwd = value
  }

  get userAuth() {
    return this.props.userAuth!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleConfirm = async () => {
    try {
      const status = await this.userAuth.authenticate(this.userAuth.userName!, this.curPwd)
      if (status) {
        Logger.devLog('- Confirm Password > Authenticated.')
        this.props.onConfirm()
        return Promise.resolve(true)
      }

      this._showErrorSnackbar('')
      return
    } catch (err) {
      this._showErrorSnackbar(err)
      return
    }
  }

  _showErrorSnackbar = (eMessage: string) => {
    this.props.globalEvt!.showEvent({
      message: this.userAuth.getSignInErrMessage(Intl, eMessage),
      evtType: 'error',
    })
  }

  _handleCancel = async () => {
    Logger.devLog('- Confirm Password > Logout.')
    await this.userAuth.signOut(true)
  }

  _handleKeyUp: ReactKeyUpEvent = (event) => {
    if (event.keyCode !== 13) return

    const $btn = document.getElementById(this._buttonId) as HTMLButtonElement
    $btn.click()
  }
}

export default PasswordCheckContainer
